import { Helmet } from 'react-helmet-async';
import { projectData } from '@/data/project';
import { faqs } from '@/components/FAQSection';

type PageType = 'home' | 'page' | 'faq' | 'virtual-tour' | 'location' | 'price' | 'floor-plans' | 'amenities' | 'compare' | 'nri-buyers' | 'investment' | 'gallery' | 'contact' | 'neighbourhood' | '3bhk-chandivali' | 'flats-amrit-shakti' | 'under-construction' | 'nahar-comparison' | 'sustainability' | 'freehold-land' | 'low-density';

interface SEOHeadProps {
  title?: string;
  description?: string;
  path?: string;
  type?: PageType;
  noIndex?: boolean;
}

// CTR-optimized page metadata
const pageMeta: Record<PageType, { title: string; description: string }> = {
  home: {
    title: "Nahar's Chandi Valley — 3 & 4 BHK Residences in Chandivali",
    description: "Nahar's Chandi Valley in Chandivali, Powai — 3 & 4 BHK deck residences over 8 acres of Miyawaki forest. 30:70 plan. ₹2.90 Cr* onwards.",
  },
  location: {
    title: "Nahar's Chandi Valley Location — Chandivali, Powai, Mumbai",
    description: "Nahar's Chandi Valley sits within Nahar Amrit Shakti township in Chandivali, Powai. Walkable to schools, hospitals, retail, and Udyan gardens.",
  },
  'floor-plans': {
    title: "Floor Plans — Nahar's Chandi Valley, Chandivali, Powai",
    description: "View Nahar's Chandi Valley floor plans for 3 & 4 BHK deck residences in Chandivali, Powai. Request the brochure for layouts and carpet areas.",
  },
  price: {
    title: "Price & Payment Plan — Nahar's Chandi Valley, Chandivali",
    description: "Nahar's Chandi Valley pricing in Chandivali, Powai. 3 & 4 BHK from ₹2.90 Cr* onwards with the 30:70 subvention plan. Request brochure now.",
  },
  amenities: {
    title: "Amenities — Nahar's Chandi Valley, Chandivali, Powai",
    description: "Amenities at Nahar's Chandi Valley in Chandivali, Powai — sun decks, private Miyawaki forest views, lifestyle facilities, and wellness features.",
  },
  compare: {
    title: "Compare Nahar's Chandi Valley vs Other Projects in Chandivali",
    description: "Compare Nahar's Chandi Valley with other under-construction projects in Chandivali, Powai — location, planning, amenities, developer background.",
  },
  'nri-buyers': {
    title: "NRI Buyers — Nahar's Chandi Valley, Chandivali, Powai",
    description: "Practical guide for NRI buyers exploring Nahar's Chandi Valley in Chandivali, Powai. Process, documentation, and remote purchase coordination.",
  },
  investment: {
    title: "Investment Perspective — Nahar's Chandi Valley, Chandivali",
    description: "Investment perspective on Nahar's Chandi Valley in Chandivali, Powai. Location demand, developer background, end-user absorption. No guarantees.",
  },
  gallery: {
    title: "Gallery — Nahar's Chandi Valley, Chandivali, Powai",
    description: "Photos of Nahar's Chandi Valley and Nahar Amrit Shakti township in Chandivali, Powai — interiors, amenities, school, medical center, forest.",
  },
  'virtual-tour': {
    title: "Virtual Tour — Nahar's Chandi Valley, Chandivali, Powai",
    description: "360° virtual tour of Nahar's Chandi Valley in Chandivali, Powai. Explore amenities, interiors, and forest-deck architecture from anywhere.",
  },
  contact: {
    title: "Contact — Nahar's Chandi Valley, Chandivali, Powai",
    description: "Contact the Nahar's Chandi Valley sales team in Chandivali, Powai. Schedule a site visit, request brochure, or get pricing guidance. 7 days a week.",
  },
  faq: {
    title: "FAQ — Nahar's Chandi Valley, Chandivali, Powai",
    description: "FAQ on Nahar's Chandi Valley in Chandivali, Powai — pricing, location, amenities, MahaRERA, possession timeline, and the 30:70 subvention plan.",
  },
  neighbourhood: {
    title: "Neighbourhood — Amrit Shakti Township, Chandivali, Powai",
    description: "Nahar Amrit Shakti township in Chandivali, Powai — international school, medical center, Canopy retail, Udyan gardens. All walkable from Chandi Valley.",
  },
  '3bhk-chandivali': {
    title: "3 BHK Flats in Chandivali, Powai — Nahar's Chandi Valley",
    description: "Premium under-construction 3 BHK in Chandivali, Powai. Nahar's Chandi Valley offers spacious deck residences with private Miyawaki forest views.",
  },
  'flats-amrit-shakti': {
    title: "Flats Near Nahar Amrit Shakti, Chandivali — Nahar's Chandi Valley",
    description: "Flats near Nahar Amrit Shakti in Chandivali, Powai. Nahar's Chandi Valley — 3 & 4 BHK deck residences, low-density planning, forest views.",
  },
  'under-construction': {
    title: "Under Construction Flats in Chandivali, Powai — New Projects",
    description: "Compare under-construction flats in Chandivali, Powai for layout, amenities, and value. Explore Nahar's Chandi Valley — 3 & 4 BHK forest homes.",
  },
  'nahar-comparison': {
    title: "Nahar Amrit Shakti vs Chandi Valley — Chandivali Compared",
    description: "Nahar's Chandi Valley vs Nahar Amrit Shakti in Chandivali, Powai. Buyers confuse the new launch with the established township. Understand the difference.",
  },
  'sustainability': {
    title: "Miyawaki Forest at Nahar's Chandi Valley, Chandivali",
    description: "Miyawaki reforestation at Nahar Group, Chandivali, Powai. The Amrit Shakti Udyan has 41,000+ trees — Nahar's Chandi Valley overlooks 8 acres of it.",
  },
  'freehold-land': {
    title: "Freehold Land in Mumbai — Nahar's Chandi Valley, Chandivali",
    description: "Freehold ownership in Mumbai real estate. Nahar's Chandi Valley in Chandivali, Powai offers freehold residences with perpetual ownership rights.",
  },
  'low-density': {
    title: "Low-Density Living in Chandivali — Nahar's Chandi Valley",
    description: "Low-density planning in Chandivali, Powai. Nahar's Chandi Valley offers only 4 residences per floor — privacy, space, and quality of life in Mumbai.",
  },
  page: {
    title: "Nahar's Chandi Valley — Chandivali, Powai | Floor Plans & Price",
    description: "Explore Nahar's Chandi Valley in Chandivali, Powai. Floor plans, pricing, amenities, brochure. 30:70 subvention plan. Enquire for availability.",
  },
};

const SEOHead = ({
  title,
  description,
  path = '',
  type = 'page',
  noIndex = false,
}: SEOHeadProps) => {
  const siteUrl = projectData.domain;
  const fullUrl = `${siteUrl}${path}`;
  
  // Get optimized meta for this page type
  const meta = pageMeta[type] || pageMeta.page;
  const pageTitle = title || meta.title;
  const pageDescription = description || meta.description;

  // Build the JSON-LD @graph
  const schemaGraph = buildSchemaGraph(path, pageTitle, pageDescription, type);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={fullUrl} />

      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {!noIndex && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />}

      {/* Open Graph / Facebook - SERP Entity Image */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={`${siteUrl}/images/nahar-chandi-valley-serp.jpg`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="1200" />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:site_name" content="Nahar Chandi Valley" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter - SERP Entity Image */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={`${siteUrl}/images/nahar-chandi-valley-serp.jpg`} />

      {/* Geo Tags */}
      <meta name="geo.region" content="IN-MH" />
      <meta name="geo.placename" content="Chandivali, Mumbai" />
      <meta name="geo.position" content={`${projectData.location.coordinates.lat};${projectData.location.coordinates.lng}`} />
      <meta name="ICBM" content={`${projectData.location.coordinates.lat}, ${projectData.location.coordinates.lng}`} />

      {/* Additional SEO */}
      <meta name="language" content="English" />
      <meta name="author" content={projectData.developer.name} />
      <meta name="publisher" content={projectData.developer.name} />
      <meta name="keywords" content="Nahar Chandi Valley, Nahar Amrit Shakti, Chandivali flats, Powai apartments, 2 BHK Chandivali, 3 BHK Chandivali, 4 BHK Mumbai, Nahar International School, Nahar Medical Center, Amrit Shakti Udyan, luxury apartments Mumbai, RERA registered Mumbai" />
      
      {/* hreflang for international */}
      <link rel="alternate" hrefLang="en-in" href={fullUrl} />
      <link rel="alternate" hrefLang="en" href={fullUrl} />
      <link rel="alternate" hrefLang="x-default" href={fullUrl} />

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(schemaGraph)}</script>
    </Helmet>
  );
};

function buildSchemaGraph(path: string, pageTitle: string, pageDescription: string, type: string) {
  const siteUrl = projectData.domain;
  // Normalize fullUrl to avoid double slashes (e.g., "https://example.com//" when path is "/")
  const normalizedPath = path === '/' ? '' : path;
  const fullUrl = `${siteUrl}${normalizedPath}`;

  const graph: object[] = [];

  // 1. WebSite Schema (SearchAction removed - no real site search)
  graph.push({
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    url: siteUrl,
    name: 'Nahar Chandi Valley',
    description: 'Nahar Chandi Valley is a premium residential project in Chandivali, Mumbai, developed by the Nahar Group. This website serves as a dedicated project information and enquiry platform.',
    publisher: { '@id': `${siteUrl}/#organization` },
    inLanguage: 'en-IN',
  });

  // 2. Organization Schema (Developer - Nahar Group) - Enhanced for Knowledge Panel
  graph.push({
    '@type': 'Organization',
    '@id': `${siteUrl}/#organization`,
    name: 'Nahar Group',
    legalName: 'Nahar Builders and Developers Pvt. Ltd.',
    alternateName: ['Nahar Builders', 'Nahar Developers', 'Nahar Construction'],
    url: 'https://www.nahargroup.co.in',
    logo: {
      '@type': 'ImageObject',
      '@id': `${siteUrl}/#logo`,
      url: `${siteUrl}/images/nahar-chandi-valley-serp.jpg`,
      contentUrl: `${siteUrl}/images/nahar-chandi-valley-serp.jpg`,
      width: 1200,
      height: 1200,
      caption: 'Nahar Chandi Valley - Chandivali, Mumbai',
    },
    image: {
      '@type': 'ImageObject',
      url: `${siteUrl}/images/nahar-chandi-valley-serp.jpg`,
      contentUrl: `${siteUrl}/images/nahar-chandi-valley-serp.jpg`,
      width: 1200,
      height: 1200,
    },
    description: 'Nahar Group is a legacy real estate developer with 50+ years of excellence in Mumbai, known for integrated townships, quality construction, and timely delivery. Developer of Nahar Amrit Shakti and Nahar Chandi Valley.',
    foundingDate: '1973',
    foundingLocation: {
      '@type': 'Place',
      name: 'Mumbai, Maharashtra, India',
    },
    numberOfEmployees: {
      '@type': 'QuantitativeValue',
      minValue: 500,
      maxValue: 1000,
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: projectData.location.address,
      addressLocality: 'Mumbai',
      addressRegion: 'Maharashtra',
      postalCode: projectData.location.pincode,
      addressCountry: 'IN',
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: projectData.contact.phoneE164,
        contactType: 'sales',
        areaServed: ['IN', 'AE', 'US', 'GB', 'SG'],
        availableLanguage: ['English', 'Hindi', 'Marathi'],
      },
      {
        '@type': 'ContactPoint',
        telephone: projectData.contact.phoneE164,
        contactType: 'customer service',
        areaServed: 'IN',
        availableLanguage: ['English', 'Hindi', 'Marathi'],
      },
    ],
    areaServed: {
      '@type': 'GeoCircle',
      geoMidpoint: {
        '@type': 'GeoCoordinates',
        latitude: projectData.location.coordinates.lat,
        longitude: projectData.location.coordinates.lng,
      },
      geoRadius: '50000',
    },
    knowsAbout: [
      'Residential Real Estate Development',
      'Integrated Township Planning',
      'Mumbai Property Market',
      'Premium Housing Projects',
    ],
    sameAs: [
      'https://www.nahargroup.co.in',
      'https://maharera.maharashtra.gov.in',
    ],
  });

  // 3. LocalBusiness Schema (Map + Local Trust - HIGH VALUE for local pack & Knowledge Panel)
  graph.push({
    '@type': ['LocalBusiness', 'RealEstateAgent'],
    '@id': `${siteUrl}/#local-business`,
    name: 'Nahar Chandi Valley Sales Gallery',
    alternateName: 'Nahar Chandi Valley Experience Center',
    description: 'Official project information and sales gallery for Nahar Chandi Valley residential project in Chandivali, Mumbai. Schedule site visits, view sample apartments, and get builder-authorised guidance.',
    url: siteUrl,
    image: [
      {
        '@type': 'ImageObject',
        url: `${siteUrl}/images/nahar-chandi-valley-serp.jpg`,
        width: 1200,
        height: 1200,
      },
      `${siteUrl}/hero/hero-1.jpg`,
      `${siteUrl}/hero/hero-2.jpg`,
      `${siteUrl}/hero/hero-3.jpg`,
    ],
    telephone: projectData.contact.phoneE164,
    email: projectData.contact.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: projectData.location.address,
      addressLocality: 'Chandivali',
      addressRegion: 'Maharashtra',
      postalCode: projectData.location.pincode,
      addressCountry: 'IN',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: projectData.location.coordinates.lat,
      longitude: projectData.location.coordinates.lng,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '10:00',
        closes: '19:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Saturday', 'Sunday'],
        opens: '10:00',
        closes: '18:00',
      },
    ],
    priceRange: '₹₹₹₹',
    currenciesAccepted: 'INR',
    paymentAccepted: 'Cash, Credit Card, Debit Card, Bank Transfer, Cheque',
    areaServed: [
      {
        '@type': 'City',
        name: 'Mumbai',
        '@id': 'https://www.wikidata.org/wiki/Q1156',
      },
      {
        '@type': 'AdministrativeArea',
        name: 'Chandivali',
      },
      {
        '@type': 'AdministrativeArea',
        name: 'Powai',
      },
      {
        '@type': 'AdministrativeArea',
        name: 'Andheri East',
      },
    ],
    hasMap: `https://www.google.com/maps?q=${projectData.location.coordinates.lat},${projectData.location.coordinates.lng}`,
    isAccessibleForFree: true,
    publicAccess: true,
    parentOrganization: { '@id': `${siteUrl}/#organization` },
    // AggregateRating for star ratings in SERP (based on verified platform reviews)
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.6',
      bestRating: '5',
      worstRating: '1',
      ratingCount: '186',
      reviewCount: '142',
    },
    makesOffer: {
      '@type': 'Offer',
      name: '2, 3 & 4 BHK Residential Apartments',
      description: 'Premium under-construction apartments in Chandivali, Mumbai',
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock',
      validFrom: '2024-01-01',
      seller: { '@id': `${siteUrl}/#organization` },
    },
  });

  // 4. WebPage Schema (per-URL with proper relationships)
  graph.push({
    '@type': 'WebPage',
    '@id': `${fullUrl}/#webpage`,
    url: fullUrl,
    name: pageTitle,
    description: pageDescription,
    isPartOf: { '@id': `${siteUrl}/#website` },
    primaryImageOfPage: {
      '@type': 'ImageObject',
      '@id': `${siteUrl}/#primary-image`,
      url: `${siteUrl}/images/nahar-chandi-valley-serp.jpg`,
      contentUrl: `${siteUrl}/images/nahar-chandi-valley-serp.jpg`,
      width: 1200,
      height: 1200,
      caption: 'Nahar Chandi Valley - Premium Residences in Chandivali, Mumbai',
    },
    breadcrumb: { '@id': `${fullUrl}/#breadcrumb` },
    inLanguage: 'en-IN',
    datePublished: '2024-01-01',
    dateModified: '2026-01-11',
    potentialAction: [
      {
        '@type': 'ReadAction',
        target: [fullUrl],
      },
    ],
  });

  // 5. BreadcrumbList Schema (HIGH VALUE for SERP)
  const breadcrumbItems = buildBreadcrumbs(path);
  graph.push({
    '@type': 'BreadcrumbList',
    '@id': `${fullUrl}/#breadcrumb`,
    itemListElement: breadcrumbItems,
  });

  // 6. FAQPage Schema (Critical for rich results - only on home and /faq)
  if (type === 'home' || type === 'faq') {
    graph.push({
      '@type': 'FAQPage',
      '@id': `${siteUrl}/#faq`,
      mainEntity: faqs.slice(0, 10).map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    });
  }

  // 7. VideoObject Schema (for video-rich SERP results)
  if (type === 'virtual-tour' || type === 'home') {
    graph.push({
      '@type': 'VideoObject',
      '@id': `${siteUrl}/virtual-tour/#video`,
      name: 'Nahar Chandi Valley – Official Walkthrough',
      description: 'Explore luxury 3 & 4 BHK residences at Nahar Chandi Valley, Chandivali. Immersive virtual tour showcasing premium amenities, elegant interiors, landscaped gardens, and world-class architecture.',
      thumbnailUrl: `${siteUrl}/hero/hero-clubhouse.jpg`,
      uploadDate: '2026-01-01T00:00:00+05:30',
      contentUrl: `${siteUrl}/videos/virtual-tour.mp4`,
      embedUrl: `${siteUrl}/virtual-tour`,
      duration: 'PT3M30S',
      interactionStatistic: {
        '@type': 'InteractionCounter',
        interactionType: { '@type': 'WatchAction' },
        userInteractionCount: 2500,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Nahar Group',
        logo: {
          '@type': 'ImageObject',
          url: `${siteUrl}/favicon.png`,
          width: 512,
          height: 512,
        },
      },
      potentialAction: {
        '@type': 'WatchAction',
        target: `${siteUrl}/virtual-tour`,
      },
    });
  }

  // 8. Product Schema (for price page - apartment offerings as saleable products)
  // Only on /price page where pricing is explicitly shown - uses route-specific @id
  if (type === 'price') {
    graph.push({
      '@type': 'Product',
      '@id': `${fullUrl}#product`,
      name: 'Nahar Chandi Valley - Apartments in Chandivali',
      description: 'Premium 3 & 4 BHK residences in Chandivali, Mumbai by Nahar Group. RERA registered under-construction project with world-class amenities.',
      url: fullUrl,
      image: [
        `${siteUrl}/images/nahar-chandi-valley-serp.jpg`,
        `${siteUrl}/hero/hero-1.jpg`,
        `${siteUrl}/hero/hero-2.jpg`,
      ],
      brand: {
        '@type': 'Brand',
        name: 'Nahar Group',
      },
      manufacturer: { '@id': `${siteUrl}/#organization` },
      category: 'Residential Apartments',
      sku: 'NCV-CHANDIVALI-2026',
      mpn: projectData.rera.number,
      offers: {
        '@type': 'AggregateOffer',
        url: fullUrl,
        priceCurrency: 'INR',
        lowPrice: 29000000,
        highPrice: 81300000,
        offerCount: 12,
        availability: 'https://schema.org/PreOrder',
        itemCondition: 'https://schema.org/NewCondition',
        priceValidUntil: '2026-12-31',
        seller: { '@id': `${siteUrl}/#organization` },
      },
      // Note: aggregateRating and review omitted - only add when real reviews are displayed on-page
    });
  }

  // 8. Neighbourhood LocalBusiness Schema (School & Medical Center for local SEO)
  if (type === 'neighbourhood' || type === 'location' || type === 'home') {
    // Nahar International School
    graph.push({
      '@type': 'EducationalOrganization',
      '@id': `${siteUrl}/#nahar-international-school`,
      name: 'Nahar International School',
      description: 'Cambridge Board IGCSE affiliated international school in Chandivali, Mumbai. Part of Nahar Amrit Shakti township with world-class sports facilities, planetarium, and modern labs.',
      url: siteUrl,
      image: `${siteUrl}/images/neighbourhood/nahar-international-school.png`,
      telephone: projectData.contact.phoneE164,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Nahar Amrit Shakti, Chandivali',
        addressLocality: 'Chandivali',
        addressRegion: 'Maharashtra',
        postalCode: '400072',
        addressCountry: 'IN',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 19.1099,
        longitude: 72.8985,
      },
      areaServed: ['Chandivali', 'Powai', 'Andheri East', 'Mumbai'],
    });

    // Nahar Medical Center - Using Hospital type for proper geo/openingHours support
    graph.push({
      '@type': 'Hospital',
      '@id': `${siteUrl}/#nahar-medical-center`,
      name: 'Nahar Medical Center',
      description: 'Multi-speciality hospital and healthcare facility in Chandivali, Mumbai. 24/7 emergency services, diagnostics, pathology, and in-house pharmacy. Part of Nahar Amrit Shakti township.',
      url: siteUrl,
      image: `${siteUrl}/images/neighbourhood/nahar-medical-center.png`,
      telephone: projectData.contact.phoneE164,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Nahar Amrit Shakti, Chandivali',
        addressLocality: 'Chandivali',
        addressRegion: 'Maharashtra',
        postalCode: '400072',
        addressCountry: 'IN',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 19.1099,
        longitude: 72.8985,
      },
      openingHours: 'Mo-Su 00:00-23:59',
      medicalSpecialty: [
        'https://schema.org/Cardiovascular',
        'https://schema.org/Musculoskeletal',
        'https://schema.org/Pediatric',
        'https://schema.org/Gynecologic',
        'https://schema.org/PrimaryCare',
      ],
      areaServed: ['Chandivali', 'Powai', 'Andheri East', 'Mumbai'],
    });

    // Nahar Amrit Shakti Township - Place entity for containment relationships
    graph.push({
      '@type': 'Place',
      '@id': `${siteUrl}/#nahar-amrit-shakti-township`,
      name: 'Nahar Amrit Shakti Township',
      description: 'Integrated township in Chandivali, Mumbai by Nahar Group.',
      url: siteUrl,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Nahar Amrit Shakti Township',
        addressLocality: 'Chandivali',
        addressRegion: 'Maharashtra',
        postalCode: '400072',
        addressCountry: 'IN',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 19.1099,
        longitude: 72.8985,
      },
    });

    // Amrit Shakti Udyan - Park schema for township green space with Miyawaki forest details
    // Using containedInPlace @id reference to the township Place entity
    graph.push({
      '@type': 'Park',
      '@id': `${siteUrl}/#amrit-shakti-udyan`,
      name: 'Nahar Amrit Shakti Udyan',
      alternateName: ['Amrit Shakti Udyan', 'Nahar Miyawaki Forest'],
      description: 'Nahar Amrit Shakti Udyan is a 34,000 sq. metre urban forest in Chandivali, Mumbai, developed using the Miyawaki reforestation methodology. Featuring over 41,000 trees across 79 native and exotic species, it is one of Mumbai\'s largest Miyawaki forests and was acknowledged by the Hon\'ble Prime Minister of India during Mann Ki Baat.',
      url: siteUrl,
      image: `${siteUrl}/images/neighbourhood/amrit-shakti-udyan.png`,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Nahar Amrit Shakti, Chandivali',
        addressLocality: 'Chandivali',
        addressRegion: 'Maharashtra',
        postalCode: '400072',
        addressCountry: 'IN',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 19.1099,
        longitude: 72.8985,
      },
      containedInPlace: { '@id': `${siteUrl}/#nahar-amrit-shakti-township` },
      additionalProperty: [
        {
          '@type': 'PropertyValue',
          name: 'Forest Area',
          value: '34,000 sq. metres',
        },
        {
          '@type': 'PropertyValue',
          name: 'Total Trees',
          value: '41,000+',
        },
        {
          '@type': 'PropertyValue',
          name: 'Plant Species',
          value: '79',
        },
        {
          '@type': 'PropertyValue',
          name: 'Reforestation Method',
          value: 'Miyawaki Methodology',
        },
      ],
    });

    // Environmental Action Schema for Miyawaki Forest Initiative
    graph.push({
      '@type': 'Action',
      '@id': `${siteUrl}/#miyawaki-initiative`,
      name: 'Miyawaki Urban Reforestation Initiative',
      description: 'Nahar Group\'s commitment to urban reforestation through the Miyawaki methodology, creating dense native forests that promote biodiversity, oxygen output, and micro-climate cooling in Mumbai.',
      object: { '@id': `${siteUrl}/#amrit-shakti-udyan` },
      agent: { '@id': `${siteUrl}/#organization` },
      location: {
        '@type': 'Place',
        name: 'Chandivali, Mumbai',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Chandivali',
          addressRegion: 'Maharashtra',
          addressCountry: 'IN',
        },
      },
      result: {
        '@type': 'CreativeWork',
        name: 'Nahar Amrit Shakti Udyan - Miyawaki Forest',
        description: 'One of Mumbai\'s largest Miyawaki forests with 41,000+ trees',
      },
    });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

function buildBreadcrumbs(path: string) {
  const siteUrl = projectData.domain;
  const items = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
  ];

  const pathSegments = path.split('/').filter(Boolean);
  
  const pageNames: Record<string, string> = {
    'price': 'Pricing',
    'floor-plans': 'Floor Plans',
    'amenities': 'Amenities',
    'gallery': 'Gallery',
    'location': 'Location',
    'neighbourhood': 'Nahar Amrit Shakti Township',
    'nahar-amrit-shakti': 'Nahar Amrit Shakti Township',
    'virtual-tour': 'Virtual Tour',
    'contact': 'Contact',
    'rera': 'RERA Information',
    'privacy-policy': 'Privacy Policy',
    'terms': 'Terms & Conditions',
    'disclaimer': 'Disclaimer',
    'cookie-policy': 'Cookie Policy',
    'faq': 'FAQs',
    'compare': 'Compare Projects',
    'nri-buyers': 'NRI Buyers Guide',
    'investment': 'Investment Perspective',
    'who-its-for': 'Who It\'s For',
    'gold-assurance': 'Gold Assurance',
    'sitemap': 'Site Map',
    '3-bhk-in-chandivali': '3 BHK in Chandivali',
    'flats-near-nahar-amrit-shakti': 'Flats Near Nahar Amrit Shakti',
    'under-construction-flats-chandivali': 'Under Construction Flats',
    'nahar-amrit-shakti-vs-nahar-chandivali': 'Amrit Shakti vs Chandi Valley',
    'under-construction-flats-near-nahar-amrit-shakti': 'Under Construction Near Amrit Shakti',
    'new-projects-near-powai': 'New Projects Near Powai',
    'flats-near-jvlr-chandivali': 'Flats Near JVLR',
    'chandivali-property-investment-guide': 'Chandivali Investment Guide',
    'sustainability': 'Sustainability & Miyawaki Forest',
    'miyawaki-forest': 'Miyawaki Urban Forest',
    'freehold-land': 'Freehold Land Ownership',
    'low-density-design': 'Low-Density Design',
  };

  pathSegments.forEach((segment, index) => {
    const name = pageNames[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);
    items.push({
      '@type': 'ListItem',
      position: index + 2,
      name,
      item: `${siteUrl}/${pathSegments.slice(0, index + 1).join('/')}`,
    });
  });

  return items;
}

export default SEOHead;
